import { disableLoader, enableLoader } from '@context/actions';
import { isNotFoundError, normalizeResourceError, ResourceError } from '@util/apiError';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

export type SharedLinkFetchState =
    | 'idle'
    | 'loading'
    | 'ready'
    | 'notFound'
    | 'error';

type SharedLinkResourceOptions<T> = {
    id?: string;
    fetcher: (id: string) => Promise<T>;
    enabled?: boolean;
    useGlobalLoader?: boolean;
    refreshKey?: any;
    /**
     * Prefetched on the server for this same id. When present the client skips its own request, so
     * the page renders even on networks that block or throttle the API.
     */
    initialData?: T | null;
};

type SharedLinkResourceResult<T> = {
    data: T | null;
    state: SharedLinkFetchState;
    error: ResourceError | null;
    retry: () => void;
};

export const useSharedLinkId = (segmentIndex = 1): string => {
    const router = useRouter();

    return useMemo(() => {
        const pagepath = router.query.pagepath;
        const pathId = Array.isArray(pagepath) ? pagepath[segmentIndex] : '';
        const queryId = Array.isArray(router.query.id)
            ? router.query.id[0]
            : router.query.id;
        return pathId || queryId || '';
    }, [router.query.pagepath, router.query.id, segmentIndex]);
};

export const useSharedLinkResource = <T>({
    id,
    fetcher,
    enabled = true,
    useGlobalLoader = true,
    refreshKey,
    initialData = null
}: SharedLinkResourceOptions<T>): SharedLinkResourceResult<T> => {
    const router = useRouter();
    const dispatch = useDispatch();
    const fetcherRef = useRef(fetcher);
    const seededData = enabled ? initialData : null;
    const [data, setData] = useState<T | null>(seededData);
    const [state, setState] = useState<SharedLinkFetchState>(seededData ? 'ready' : 'idle');
    const [error, setError] = useState<ResourceError | null>(null);
    const [reloadKey, setReloadKey] = useState(0);

    fetcherRef.current = fetcher;

    const retry = useCallback(() => setReloadKey((key) => key + 1), []);

    useEffect(() => {
        // A server-rendered payload is already the answer for this id. Only an explicit retry
        // (which bumps reloadKey) should go back to the network.
        if (enabled && initialData && reloadKey === 0) {
            setData(initialData);
            setState('ready');
            setError(null);
            if (useGlobalLoader) dispatch(disableLoader());
            return;
        }

        if (!enabled || !id) {
            setData(null);
            setError(null);
            // Query params are empty until the router hydrates, so an absent id only means the
            // resource is missing once it is ready. Reporting notFound earlier flashes an error.
            if (!enabled) setState('idle');
            else setState(router.isReady ? 'notFound' : 'loading');
            if (useGlobalLoader) dispatch(disableLoader());
            return;
        }

        let cancelled = false;
        setData(null);
        setError(null);
        setState('loading');
        if (useGlobalLoader) dispatch(enableLoader());

        fetcherRef.current(id).then((result) => {
            if (cancelled) return;
            if (!result) {
                setState('notFound');
                return;
            }
            setData(result);
            setState('ready');
        }).catch((fetchError) => {
            if (cancelled) return;
            const normalizedError = normalizeResourceError(fetchError);
            setError(normalizedError);
            setState(isNotFoundError(normalizedError) ? 'notFound' : 'error');
        }).finally(() => {
            if (!cancelled && useGlobalLoader) dispatch(disableLoader());
        });

        return () => {
            cancelled = true;
            // The `finally` above bails out once cancelled, so release the loader here instead.
            // Cleanup runs before the next effect re-enables it, so the flag stays consistent.
            if (useGlobalLoader) dispatch(disableLoader());
        };
    }, [dispatch, enabled, id, refreshKey, reloadKey, useGlobalLoader, router.isReady, initialData]);

    return { data, state, error, retry };
};
